import React from "react";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import type {IOffSeasonFormController} from "./off_season_form.controller";
import styled from "@emotion/styled";
import {Container, Grid, TextField} from "@mui/material";
import {Ad} from "views/components/ad/ad.component";
import {FormControlStyled} from "views/components/form/styles";
import {ButtonPrimary} from "views/components/buttons/buttons.component";
import {Exist} from "views/components/exist/exist.component";
import logo from "assets/img/logo.png";

const Wrapper = styled.div`
	width: 100%;
	padding: 8px 0;

	@media (max-width: 768px) {
		padding: 0;
	}
`;

const Title = styled.div`
	display: flex;
	align-items: center;
	color: #000;
	font-family: var(--fontFamilyBase);
	font-size: 40px;
	font-weight: 600;
	line-height: 120%;
	letter-spacing: -0.8px;
	text-transform: capitalize;
	gap: 8px;
	border-bottom: 1px solid transparent;

	@media (max-width: 768px) {
		font-size: 24px;
	}
`;

const Form = styled.form`
	display: flex;
	justify-content: center;
	border-radius: 4px;
	border: 1px solid #d6d4d2;
	padding: 40px;

	@media (max-width: 768px) {
		padding: 20px;
	}
`;

const Description = styled.div`
	color: #000;
	text-align: center;
	font-family: var(--fontFamilyBase);
	font-size: 16px;
	font-weight: 400;
	line-height: 150%;
	margin-bottom: 16px;

	@media (max-width: 768px) {
		font-size: 14px;
	}
`;

const ErrorBlock = styled.div`
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: 20px;
	letter-spacing: 0.4px;
	color: #e40000;
	text-align: left;
	padding: 10px 0 10px 15px;
`;

const Input = styled(TextField)`
	.MuiFormLabel-root {
		color: #757575;
		font-family: var(--fontFamilyBase), sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 150%;
		letter-spacing: 0.45px;
	}

	& label.Mui-focused {
		color: #a0aab4;
	}

	& .MuiInput-underline:after {
		border-bottom-color: #b2bac2;
	}

	&&& .MuiOutlinedInput-root {
		& fieldset {
			border-radius: 100px;
			border-color: #949494;
		}

		&:hover fieldset {
			border-color: #000;
		}

		& .Mui-focused fieldset {
			border-color: #000;
		}
	}
`;

export const OffSeasonForm: React.FC = observer(() => {
	const {i18n, error, isFormDisabled, email, isSubmitted, handleChange, handleSubmit} =
		useViewController<IOffSeasonFormController>(Bindings.OffSeasonFormController);

	return (
		<Wrapper>
			<Container>
				<Grid container spacing={3}>
					<Grid item xs={12} md={8}>
						<Title className="border-[#d6d4d2] py-4 md:py-6">
							{i18n.t("page.off_season.title", "PGA TOUR Fantasy Golf")}
						</Title>

						<Form
							className="mt-4 md:mt-6"
							onSubmit={handleSubmit}
							onChange={handleChange}
							noValidate>
							<div className="flex flex-col items-center max-w-[384px] justify-self-center">
								<img
									className="mb-4 h-[72px] md:h-[80px] w-auto"
									src={logo}
									alt=""
								/>
								<Title className="mb-3 md:text-[36px] text-center">
									{i18n.t(
										"page.off_season.form_title",
										"PGA TOUR Fantasy 2026 Coming Soon"
									)}
								</Title>
								<Description>
									{i18n.t(
										"page.off_season.form_description",
										"Please enter your email address below to be notified when the season begins."
									)}
								</Description>

								<FormControlStyled fullWidth variant="outlined" className="mb-4">
									<Input
										error={Boolean(error)}
										variant="outlined"
										id="email-input"
										disabled={isFormDisabled}
										value={email}
										name="email"
										type="email"
										label={i18n.t("page.off_season.email_label", "Email")}
										placeholder={i18n.t("page.off_season.email_label", "Email")}
										required
									/>
									<Exist when={Boolean(error)}>
										<ErrorBlock>{error}</ErrorBlock>
									</Exist>
								</FormControlStyled>

								<ButtonPrimary
									className="w-auto min-w-[156px]"
									disabled={isFormDisabled}
									type="submit">
									<Exist when={isSubmitted}>
										{i18n.t(
											"page.off_season.form_button_submitted",
											"Email sent"
										)}
									</Exist>
									<Exist when={!isSubmitted}>
										{i18n.t("page.off_season.form_button", "Notify Me")}
									</Exist>
								</ButtonPrimary>
							</div>
						</Form>

						<Ad
							divId="fantasy-midcontent"
							baseDivId="pb-slot-midcontent"
							pos="midcontent"
						/>
					</Grid>
					<Grid item xs={12} md={4} className="m_hide">
						<Ad divId="fantasy-ticker" baseDivId="pb-slot-ticker" pos="tickerspon" />
					</Grid>
				</Grid>
			</Container>
		</Wrapper>
	);
});
